import type { StorePageItem, StorePageMeta } from '@/lib/api/commerce/store-themes'
import { useEffect, useState } from 'react'
import { MdDelete, MdSave } from 'react-icons/md'
import ThemeFieldInput, { type ThemeFieldOptions } from './theme-field'

interface PageSettingsProps {
    page: StorePageItem
    options: ThemeFieldOptions
    saving: boolean
    onSave: (meta: StorePageMeta) => void
    onDelete: () => void
}

const SEO_TITLE_MAX = 70
const SEO_DESCRIPTION_MAX = 300

/**
 * Título, endereço, visibilidade e SEO da página. Diferente das seções, valem ao
 * salvar (não esperam a publicação): mudar o endereço de uma página publicada muda o
 * link na hora.
 */
export default function PageSettings({ page, options, saving, onSave, onDelete }: PageSettingsProps) {
    const [form, setForm] = useState({
        title: page.title,
        slug: page.slug,
        visible: page.visible,
        showInHeader: page.showInHeader ?? false,
        showInSidebar: page.showInSidebar ?? false,
        showInFooter: page.showInFooter ?? true,
        seoTitle: page.seoTitle ?? '',
        seoDescription: page.seoDescription ?? '',
        seoImage: page.seoImage ?? '',
    })

    useEffect(() => {
        setForm({
            title: page.title,
            slug: page.slug,
            visible: page.visible,
            showInHeader: page.showInHeader ?? false,
            showInSidebar: page.showInSidebar ?? false,
            showInFooter: page.showInFooter ?? true,
            seoTitle: page.seoTitle ?? '',
            seoDescription: page.seoDescription ?? '',
            seoImage: page.seoImage ?? '',
        })
    }, [page])

    const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
        setForm((current) => ({ ...current, [key]: value }))

    const changed =
        form.title !== page.title ||
        form.slug !== page.slug ||
        form.visible !== page.visible ||
        form.showInHeader !== (page.showInHeader ?? false) ||
        form.showInSidebar !== (page.showInSidebar ?? false) ||
        form.showInFooter !== (page.showInFooter ?? true) ||
        form.seoTitle !== (page.seoTitle ?? '') ||
        form.seoDescription !== (page.seoDescription ?? '') ||
        form.seoImage !== (page.seoImage ?? '')

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Título, endereço e SEO</h3>
                {!page.publishedAt && <span className="badge badge-warning badge-sm">Ainda não publicada</span>}
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Título</span>
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={120}
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Endereço</span>
                    <label className="input input-bordered flex w-full items-center gap-1">
                        <span className="text-base-content/50">/</span>
                        <input
                            type="text"
                            className="grow"
                            maxLength={60}
                            value={form.slug}
                            onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                        />
                    </label>
                    <span className="text-xs text-base-content/60">
                        Letras minúsculas, números e hífen. Ex.: trocas-e-devolucoes
                    </span>
                </div>
                <label className="flex cursor-pointer items-center justify-between gap-4 md:col-span-2">
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">Mostrar na loja</span>
                        <span className="text-xs text-base-content/60">
                            Escondida, a página não aparece no rodapé e o endereço não abre. Continua na prévia.
                        </span>
                    </span>
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={form.visible}
                        onChange={(e) => set('visible', e.target.checked)}
                    />
                </label>
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-sm font-medium">Onde o link aparece</span>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {(
                        [
                            ['showInHeader', 'Topo', 'Faixa no cabeçalho, acima das categorias.'],
                            ['showInSidebar', 'Lateral', 'Antes das categorias. Só nas disposições com barra lateral.'],
                            ['showInFooter', 'Rodapé', 'Lista de links no rodapé da loja.'],
                        ] as const
                    ).map(([key, label, help]) => (
                        <label
                            key={key}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${form[key] ? 'border-primary bg-primary/5' : 'border-base-300'}`}
                        >
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm mt-0.5"
                                checked={form[key]}
                                onChange={(e) => set(key, e.target.checked)}
                            />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">{label}</span>
                                <span className="text-xs text-base-content/60">{help}</span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="divider my-0 text-xs text-base-content/50">Buscadores e compartilhamento</div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Título nos buscadores</span>
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={SEO_TITLE_MAX}
                        placeholder={form.title}
                        value={form.seoTitle}
                        onChange={(e) => set('seoTitle', e.target.value)}
                    />
                    <span className="text-xs text-base-content/60">
                        {form.seoTitle.length}/{SEO_TITLE_MAX}. Vazio: usa o título da página.
                    </span>
                </div>
                <div className="flex flex-col gap-1.5 md:row-span-2">
                    <ThemeFieldInput
                        name="seoImage"
                        field={{ type: 'image', label: 'Imagem de compartilhamento', help: 'Aparece no WhatsApp e nas redes. Recomendado: 1200 x 630 px.' }}
                        value={form.seoImage}
                        onChange={(value) => set('seoImage', (value as string) ?? '')}
                        options={options}
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Descrição nos buscadores</span>
                    <textarea
                        className="textarea textarea-bordered w-full"
                        rows={3}
                        maxLength={SEO_DESCRIPTION_MAX}
                        value={form.seoDescription}
                        onChange={(e) => set('seoDescription', e.target.value)}
                    />
                    <span className="text-xs text-base-content/60">
                        {form.seoDescription.length}/{SEO_DESCRIPTION_MAX}. O Google costuma mostrar até 160.
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-base-200 pt-4">
                <button type="button" className="btn btn-sm btn-ghost text-error" onClick={onDelete}>
                    <MdDelete /> Remover página
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!changed || saving || !form.title.trim() || !form.slug}
                    onClick={() =>
                        onSave({
                            title: form.title,
                            slug: form.slug.replace(/^-+|-+$/g, ''),
                            visible: form.visible,
                            showInHeader: form.showInHeader,
                            showInSidebar: form.showInSidebar,
                            showInFooter: form.showInFooter,
                            seoTitle: form.seoTitle || null,
                            seoDescription: form.seoDescription || null,
                            seoImage: form.seoImage || null,
                        })
                    }
                >
                    {saving ? <span className="loading loading-spinner loading-xs" /> : <MdSave />} Salvar
                </button>
            </div>
        </div>
    )
}
